import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type FloorResponse, type LayoutResponse, ApiError } from '../lib/api.js';
import { useSession, useVenueId } from '../lib/session.js';
import { useVenue } from '../hooks/useVenue.js';
import { currentServiceDate, minutesToClock, clockToMinutes } from '../lib/time.js';
import { FloorCanvas } from '../components/FloorCanvas.js';
import { ServiceDatePicker } from '../components/ServiceDatePicker.js';
import { TableStatusLegend } from '../components/TableStatusLegend.js';

/**
 * The floor view. The screen a host actually stands in front of.
 *
 * Three things it has to get right:
 *  - It MIRRORS in Arabic. Not the text — the room. See FloorCanvas.
 *  - The time scrubber reaches past midnight, because suhoor is part of the
 *    same service and a host at 01:30 is still working the evening that began
 *    the previous day.
 *  - Dragging a booking onto a table seats or moves it, and the engine's
 *    objections are warnings rather than refusals.
 */
export function FloorScreen() {
  const { t, i18n } = useTranslation();
  const venueId = useVenueId();
  const venue = useVenue();
  const { can } = useSession();
  const queryClient = useQueryClient();

  const [serviceDate, setServiceDate] = useState(() =>
    venue ? currentServiceDate(venue) : '',
  );
  /** Null means "now" — the screen follows the clock until a host scrubs. */
  const [scrubMinutes, setScrubMinutes] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const date = serviceDate || (venue ? currentServiceDate(venue) : '');

  const floor = useQuery({
    queryKey: ['floor', venueId, date, scrubMinutes],
    enabled: Boolean(venue && date),
    queryFn: () =>
      api.get<FloorResponse>('/api/v1/floor', {
        venue_id: venueId,
        date,
        ...(scrubMinutes === null ? {} : { time: minutesToClock(scrubMinutes) }),
      }),
    // Live while following the clock; static once a host has scrubbed to a
    // moment they are reasoning about.
    refetchInterval: scrubMinutes === null ? 15_000 : false,
  });

  const layout = useQuery({
    queryKey: ['layout', venueId],
    enabled: Boolean(venueId),
    queryFn: () => api.get<LayoutResponse>('/api/v1/floor/layout', { venue_id: venueId }),
    staleTime: 5 * 60_000,
  });

  const move = useMutation({
    mutationFn: (input: { reservationId: string; tableIds: string[]; version?: number }) =>
      api.post(`/api/v1/reservations/${input.reservationId}/move`, {
        table_ids: input.tableIds,
        ...(input.version === undefined ? {} : { version: input.version }),
        // The host has looked at the room. Their judgement stands over the
        // engine's — it warns, it does not block (D-034).
        acknowledge_warnings: true,
      }),
    onSuccess: () => {
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['floor'] });
    },
    onError: (err: unknown) => {
      setError(
        err instanceof ApiError && err.isConflict
          ? err.message
          : t('errors.generic'),
      );
    },
  });

  const seat = useMutation({
    mutationFn: (input: { reservationId: string; version: number }) =>
      api.post(`/api/v1/reservations/${input.reservationId}/seat`, { version: input.version }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['floor'] }),
  });

  const handleDrop = useCallback(
    (reservationId: string, tableId: string) => {
      const table = floor.data?.tables.find((row) => row.reservation?.id === reservationId);
      move.mutate({
        reservationId,
        tableIds: [tableId],
        ...(table?.reservation ? {} : {}),
      });
    },
    [floor.data, move],
  );

  const shiftBounds = useMemo(() => {
    // A generous default so the scrubber covers a full service including
    // suhoor: 06:00 through 30:00 is 06:00 today to 06:00 tomorrow.
    return { from: 6 * 60, to: 30 * 60 };
  }, []);

  const currentMinutes =
    scrubMinutes ?? (floor.data ? clockToMinutes(floor.data.clock) : 12 * 60);

  const covers = useMemo(
    () =>
      (floor.data?.tables ?? []).reduce(
        (sum, table) => sum + (table.reservation?.party_size ?? 0),
        0,
      ),
    [floor.data],
  );

  if (!venue) return <p className="text-sm text-slate-500">{t('app.loading')}</p>;

  return (
    <div className="flex flex-col gap-4">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-semibold">{t('floor.title')}</h1>
          <ServiceDatePicker value={date} onChange={setServiceDate} venue={venue} />
        </div>

        <div className="flex items-center gap-4">
          <div className="text-end">
            <div className="text-xs uppercase tracking-wide text-slate-500">
              {t('floor.covers')}
            </div>
            <div className="font-mono text-xl" data-numeric>
              {covers}
            </div>
          </div>
          <TableStatusLegend />
        </div>
      </header>

      {/* The scrubber. Its range runs past 24:00 on purpose - a host working
          suhoor at 01:30 is still in the evening that started yesterday, and a
          scrubber that stopped at midnight would refuse to show it. */}
      <section className="rounded-lg border border-slate-200 bg-white p-3">
        <div className="flex items-center gap-3">
          <label className="text-xs uppercase tracking-wide text-slate-500" htmlFor="scrub">
            {t('floor.scrubber')}
          </label>
          <input
            id="scrub"
            type="range"
            className="h-2 flex-1 cursor-pointer accent-brand-teal"
            min={shiftBounds.from}
            max={shiftBounds.to}
            step={15}
            value={currentMinutes}
            onChange={(event) => setScrubMinutes(Number(event.target.value))}
          />
          <span className="w-16 text-center font-mono text-sm" data-numeric>
            {minutesToClock(currentMinutes, true)}
          </span>
          <button
            type="button"
            onClick={() => setScrubMinutes(null)}
            className={[
              'rounded-md px-3 py-1 text-sm font-medium transition-colors',
              scrubMinutes === null
                ? 'bg-brand-teal text-white'
                : 'border border-slate-200 text-slate-600 hover:bg-brand-teal-light',
            ].join(' ')}
          >
            {t('floor.now')}
          </button>
        </div>
      </section>

      {error ? (
        <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">
          {error}
        </p>
      ) : null}

      {floor.data?.issues.length ? (
        <section className="rounded-lg border border-amber-300 bg-amber-50 p-3">
          <h2 className="text-sm font-semibold text-amber-900">{t('floor.issues')}</h2>
          <ul className="mt-1 space-y-1 text-sm text-amber-900">
            {floor.data.issues.map((issue, index) => (
              <li key={`${issue.kind}-${index}`}>{issue.message}</li>
            ))}
          </ul>
        </section>
      ) : null}

      {floor.isLoading || layout.isLoading ? (
        <p className="text-sm text-slate-500">{t('app.loading')}</p>
      ) : null}

      {floor.data && layout.data ? (
        <FloorCanvas
          tables={floor.data.tables}
          layout={layout.data}
          locale={i18n.language}
          canMove={can('reservation:move')}
          onDropBooking={handleDrop}
          onSeat={(reservationId, version) => seat.mutate({ reservationId, version })}
        />
      ) : null}
    </div>
  );
}
